<template>
  <div class="relative h-full w-full overflow-hidden">
    <!-- A video URL down the image pipeline is a guaranteed 400: NuxtImg's cloudinary provider is
         pinned to /image/upload. The clip's first frame is a real image, so serve that instead. -->
    <img
      v-if="videoPoster"
      :src="videoPoster"
      :alt="alt"
      :class="imageClass"
      :width="width"
      :height="height"
      :fetchpriority="fetchpriority"
      :loading="loading"
      decoding="async"
    />
    <NuxtImg
      v-else
      :src="normalizeUrl"
      :alt="alt"
      :sizes="sizes"
      :class="imageClass"
      :preload="isCritical"
      :fetchpriority="fetchpriority"
      :width="width"
      :height="height"
      :fit="fit"
      :loading="loading"
      provider="cloudinary"
      format="webp"
      :placeholder="isCritical ? false : placeholder"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  DEFAULT_VIDEO_WIDTH,
  cloudinaryVideoPoster,
  isCloudinaryVideo,
} from '../../libs/cloudinaryVideo'

const props = defineProps({
  imageClass: [String, Array],
  url: String,
  alt: String,
  sizes: {
    type: String,
    default: 'xs:100vw sm:100vw md:100vw lg:1200px xl:1200px xxl:1200px',
  },
  width: {
    type: Number,
  },
  height: {
    type: Number,
  },
  fit: {
    type: String,
    default: 'cover',
  },
  fetchpriority: {
    type: String,
    default: 'auto',
  },
  loading: {
    type: String,
    default: 'lazy',
  },
  placeholder: {
    type: Boolean,
    default: true,
  },
})

/** Empty unless the CMS field handed us a clip, which the Cloudinary widget allows anywhere. */
const videoPoster = computed(() =>
  isCloudinaryVideo(props.url)
    ? cloudinaryVideoPoster(props.url, props.width || DEFAULT_VIDEO_WIDTH)
    : '',
)

const isCritical = computed(() => {
  return props.loading === 'eager' || props.fetchpriority === 'high'
})

const normalizeUrl = computed(() => {
  if (!props.url) return ''

  const httpsUrl = props.url.replace(/^http:\/\//i, 'https://')

  const uploadIndex = httpsUrl.indexOf('/image/upload/')
  if (uploadIndex === -1) {
    return httpsUrl
  }

  const afterUpload = httpsUrl.slice(uploadIndex + '/image/upload/'.length)
  const withoutExt = afterUpload.replace(/\.[a-zA-Z0-9]+$/, '')
  const publicIdMatch = withoutExt.match(/djurensratt-se\/.*$/)

  // If the public ID matches the expected pattern, return it
  if (publicIdMatch) {
    return publicIdMatch[0]
  }

  // If the pattern doesn't match, return the URL without the extension
  const versionIndex = withoutExt.indexOf('/v')
  if (versionIndex !== -1) {
    const afterVersion = withoutExt.slice(versionIndex)
    return afterVersion.replace(/^\/v[0-9]+\//, '')
  }

  // If no version is found, return the URL without the extension
  return withoutExt
})
</script>
